import { ChangeMode } from './../../enums/InputProvider';
import CameraService, {
	CameraPermissionState,
	CameraFacingMode,
} from '../../../helpers/classes/CameraService';
import EventService from '../../../helpers/classes/EventService';
import TemplateService from '../../../helpers/classes/TemplateService';
import DataService, { DataStoreType } from '../../classes/DataService';
import { JourneyContainer } from '../../classes/JourneyContainer';
import { JourneyEvent } from '../../enums/JourneyEvent';
import { Templates } from '../../enums/Templates';
import { UploadResponse } from '../../interfaces/UploadResponse';
import { IIdScanData } from '../../interfaces/DataFormats';
import { IExtendedDocument } from '../../interfaces/Document';
import { removeNullOrUndefined } from '../../../helpers/functions/object';
import { DocumentSource } from '../../enums/DocumentSource';
import RenderingService from '../../../helpers/classes/RenderingService';
import LanguageService from '../../../helpers/classes/LanguageService';
import { TranslationKey } from '../../enums/Translation';
import { HintType, ReticleType } from '../../enums/Camera';
import { ImageProvider } from '../provider/image';
import HttpService from '../../../helpers/classes/HttpService';
import * as lottie from 'lottie-web/build/player/lottie';
import { AnimationService } from '../../../helpers/classes/AnimationService';
import { InputProvider } from '../../enums/InputProvider';

export class PassiveLivenessStep {
	public static render(this: JourneyContainer) {
		return CameraService.getVideoCameras()
			.then((cameras) =>
				cameras.map((camera) => ({
					name: camera.label,
					value: camera.deviceId,
				}))
			)
			.then((cameras) => {
				PassiveLivenessStep.cameras = cameras;
				CameraService.getVideoCameras()
				Promise.all([
					CameraService.getCameraPermission(),
					CameraService.hasDeviceCapableOfResolution()
				]).then(([permission]) => {

					switch (permission) {
						case CameraPermissionState.DENIED:
							return TemplateService.renderToContainer(this._container, Templates.HasNoCamera)
								.then(() => {
									this.hookGlobalButtons(false);
								})
							break;
					}
				})
				return TemplateService.renderToContainer(
					this._container,
					Templates.PassiveLiveness,
					{
						cameras,
					}

				);

			})
			.then((container) =>
				this.changeCameraStream(
					undefined,
					ReticleType.PORTRAIT,
					!this._config.hideLogo,
					HintType.NONE,
					true,
					CameraFacingMode.FRONT
				).catch((e) => console.log(e)).then(() => container))
			.then((container) => {
				TemplateService.getElements(container, {
					cameraClick: '[data-jcs-element="camera__capture"]',
					status: '[data-jcs-element="view__status"]',
					statusMessage: '[data-jcs-element="view__status__message"]',
					cameraSelect: '[data-jcs-element="camera__select"]',
					messages: '[data-jcs-element="passive__liveness__message"]',
					messagesTitle: '[data-jcs-element="passive__liveness__message__title"]',
					messagesContainer: '[data-jcs-element="passive__liveness__message__container"]',
					mobileHelp: '[data-jcs-element="mobile__help__container"]',
					desktopHelp: '[data-jcs-element="desktop__help__container"]',
					mobileCloseHelp: '[data-jcs-element="mobile__close__help"]',
					mobileOpenHelp: '[data-jcs-element="mobile__open__help"]',
					desktopCloseHelp: '[data-jcs-element="desktop__close__help"]',
					desktopOpenHelp: '[data-jcs-element="desktop__open__help"]',
					passiveLivenessContainer: '[data-jcs-element="passive__liveness__step__container"]',
					passiveLivenessProviderContainer: '[data-jcs-element="provider__container"]',
					loadingAnimationContainer: '[data-jcs-element="loading-animation"]',
				}).then(({ cameraClick, cameraSelect, messages, status, statusMessage, messagesTitle, messagesContainer, mobileHelp, desktopHelp, mobileCloseHelp, mobileOpenHelp, desktopCloseHelp, desktopOpenHelp, passiveLivenessContainer, passiveLivenessProviderContainer, loadingAnimationContainer }) => {
					RenderingService.hideObject(passiveLivenessProviderContainer);
					RenderingService.setEnabled(cameraClick as HTMLInputElement, true);
					CameraService.getCameraPermission()
						.then((permission) => {
							switch (permission) {
								case CameraPermissionState.DENIED:
									TemplateService.renderToContainer(this._container, Templates.HasNoCamera);
									break;
							}
						}).catch((e) => {
							console.error(e);
							RenderingService.showObject(status as HTMLElement);
							RenderingService.setText(statusMessage as HTMLElement, e.message);
						})
					if (cameraSelect instanceof HTMLSelectElement) {
						RenderingService.setEnabled(cameraSelect, true);
						PassiveLivenessStep.loadingAnimation = AnimationService.CreateAnimation(loadingAnimationContainer as HTMLDivElement, this.assetsFolder + '/animations/loading.json', true, true, {});
						this.renderAvailableCameras(cameraSelect as HTMLSelectElement);

						cameraSelect.addEventListener('change', () => {
							RenderingService.setEnabled(cameraSelect, false);
							this.changeCameraStream(cameraSelect.value, ReticleType.PORTRAIT, undefined, undefined, true);
							RenderingService.markSelected(cameraSelect, cameraSelect.value);
							RenderingService.setEnabled(cameraSelect, true);
						});

						if (cameraSelect instanceof HTMLSelectElement) {
							RenderingService.updateSelect(cameraSelect, PassiveLivenessStep.cameras);
							if (PassiveLivenessStep.cameras && PassiveLivenessStep.cameras.length > 1) {
								RenderingService.showObject(cameraSelect);
							} else {
								RenderingService.hideObject(cameraSelect);
							}
						}
					}

					if (mobileOpenHelp instanceof HTMLElement) {
						mobileOpenHelp.addEventListener('click', () => {
							RenderingService.showObject(mobileHelp);
							RenderingService.hideObject(mobileOpenHelp);
						});
					}

					if (mobileCloseHelp instanceof HTMLElement) {
						mobileCloseHelp.addEventListener('click', () => {
							RenderingService.hideObject(mobileHelp);
							RenderingService.showObject(mobileOpenHelp);
						});
					}

					if (desktopOpenHelp instanceof HTMLElement) {
						desktopOpenHelp.addEventListener('click', () => {
							RenderingService.showObject(desktopHelp);
							RenderingService.showObject(desktopCloseHelp);
							RenderingService.hideObject(desktopOpenHelp);
						});
					}

					if (desktopCloseHelp instanceof HTMLElement) {
						desktopCloseHelp.addEventListener('click', () => {
							RenderingService.hideObject(desktopHelp);
							RenderingService.hideObject(desktopCloseHelp);
							RenderingService.showObject(desktopOpenHelp);
						});
					}
					EventService.unsubscribeTopic(JourneyEvent.PASSIVE_LIVENESS_FINISHED);
					EventService.subscribe(JourneyEvent.PASSIVE_LIVENESS_FINISHED, (response: any) => {
						RenderingService.hideObject(passiveLivenessProviderContainer);
						if (
							!response.hasError ||
							response.NextAction !== 'PASSIVE_LIVENESS'
						) {
							const data = PassiveLivenessStep.parseResponse(
								response.Workflow
							);

							DataService.getDataStore<IIdScanData>(
								DataStoreType.ID_SCAN
							).update(
								PassiveLivenessStep.parseResponseData(response.Workflow)
							);
							CameraService.stopStreaming();
							this._journey!.bindUpdate(data).then(() => this.evaluate());
						} else {
							RenderingService.showObject(passiveLivenessContainer);
							RenderingService.showObject(messagesContainer);
							RenderingService.hideObject(loadingAnimationContainer);
							RenderingService.setText(
								messagesTitle,
								LanguageService.getTranslation(
									`PASSIVE_LIVENESS_ERROR_${response.ErrorCode}_TITLE` as TranslationKey
								)
							);
							RenderingService.setText(
								messages,
								LanguageService.getTranslation(
									`PASSIVE_LIVENESS_ERROR_${response.ErrorCode}` as TranslationKey
								)
							);
							this.changeCameraStream(
								undefined,
								ReticleType.PORTRAIT,
								!this._config.hideLogo,
								HintType.NONE,
								true,
								CameraFacingMode.FRONT);
							RenderingService.setEnabled(cameraClick as HTMLInputElement, true);
						}
					});

					if (cameraClick instanceof HTMLInputElement) {
						cameraClick.addEventListener('click', async () => {
							const frame = await CameraService.getImageFromCurrentStream();
							RenderingService.showObject(loadingAnimationContainer);
							if (this._previewPages.passiveLivenessStep.manualCapture) {
								RenderingService.hideObject(messagesContainer);
								RenderingService.hideObject(passiveLivenessContainer);
								RenderingService.showObject(passiveLivenessProviderContainer);
								return ImageProvider.render.call(this, frame, []);
							} else {
								RenderingService.setEnabled(cameraClick, false);
								this.freezeCameraStream();
								RenderingService.showObject(loadingAnimationContainer);
								return PassiveLivenessStep.continueJourneySkipPreview.call(this, frame);
							}
						});
					}
				});

				return container;
			})
			.then(() =>
				EventService.publish(JourneyEvent.DISPLAY_STEP, {
					step: Templates.PassiveLiveness,
					provider: InputProvider.LIVENESS
				})
			);
	}

	private static cameras: { name: string; value: string; }[];
	private static loadingAnimation: lottie.AnimationItem | undefined;

	private static async continueJourneySkipPreview(this: JourneyContainer, image: string) {
		const response = await HttpService.post(
			HttpService.endpoints.passiveLiveness,
			JSON.stringify({
				Base64Input: image,
				JourneyId: this._journey!.id,
			}),
			undefined,
			'passiveLiveness'
		);

		return EventService.publish(JourneyEvent.PASSIVE_LIVENESS_FINISHED, response);
	}

	private static parseResponse = (data: any): UploadResponse => ({
		currentResult: data.CurrentResult,
		documentSource: DocumentSource.IDENTITY,
		entryData: {
			scanReference: data.ScanReference,
			documents: [],
			holder: { address: {} },
		},
		entryDateTime: data.EntryDateTime,
		entryImages: data.EntryImages,
		hasError: data.HasError,
		highLevelResult: data.HighLevelResult,
		isFinished: data.IsFinished,
		journeyId: '',
		personEntryId: data.PersonEntryId,
		requiredAction: data.RequiredAction,
		resultDetails: data.ResultDetails,
	})

	private static parseResponseData = ({
		EntryData,
		EntryDateTime,
		JourneyId,
		RequiredAction,
		HighLevelResult,
		CurrentResult,
		HasError,
		IsFinished,
		PersonEntryId
	}: any): Partial<IIdScanData> => ({
		journeyId: PersonEntryId,
		journeyReference: EntryData.ScanReference,
		documents: (EntryData.Documents || []).map(
			(doc: {
				expirationDate: any;
				issueDate: any;
				issueNumber: any;
				number: any;
				type: any;
			}): IExtendedDocument =>
				removeNullOrUndefined({
					expirationDate: doc.expirationDate,
					issueDate: doc.issueDate,
					issueNumber: doc.issueNumber,
					number: doc.number,
					type: doc.type,
					blurCheck: EntryData.BlurCheck === 'Good',
					glareCheck: EntryData.BlurCheck === 'Good',
					lowResolutionCheck: EntryData.LowResolutionCheck === 'Good',
					scanReference: EntryData.ScanReference,
					scannerIp: EntryData.ScannerIp,
					dateTime: EntryData ? new Date(EntryDateTime) : undefined,
				})
		),
		requiredAction: RequiredAction,
		highLevelResult: HighLevelResult,
		currentResult: CurrentResult,
		hasError: HasError,
		isFinished: IsFinished,
	})
}
